import type { Schemas } from '@ers/sdk';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api, call, ifMatch } from '../../api/client';
import { useSession } from '../../app/session';
import type { ApiQuery } from '../../components/data/apiTypes';
import { useCursorQuery } from '../../components/data/useCursorQuery';
import { useRouteProbe } from '../../components/data/useRouteProbe';

/*
 * Перенесення даних (модуль migration, розділ 11 плану): джерела й ключі доступу, файли вивантажень, завдання
 * імпорту, пробний прогін і застосування (фоновий процес — стан опитуємо кожні 2 с), реєстр проблем, журнал,
 * звірка, пошук перенесених об’єктів за старими посиланнями, архів журналу «Статистика ОЕ».
 * Типи — з контракту (@ers/sdk); ключі кешу — під префіксом ['migration']: подія потоку «migration.*» оновлює модуль.
 * Операції модуля — лише для адміністратора чи власника (область admin); остаточно права перевіряє сервер.
 */

// ——— Типи з контракту ———

export type MigrationSource = Schemas['MigrationSource'];
export type MigrationSourceKey = MigrationSource['key'];
export type MigrationCredential = Schemas['MigrationCredential'];
export type CredentialSource = MigrationCredential['source'];
export type CredentialCheck = Schemas['MigrationCredentialCheck'];
export type CredentialSetInput = Schemas['MigrationCredentialSetInput'];
export type MigrationFile = Schemas['MigrationFile'];
export type MigrationJob = Schemas['MigrationJob'];
export type MigrationRun = Schemas['MigrationRun'];
export type MigrationCounters = Schemas['MigrationCounters'];
export type MigrationIssue = Schemas['MigrationIssue'];
export type IssueSeverity = MigrationIssue['severity'];
export type IssuePage = Schemas['MigrationIssuePage'];
export type IssueSummary = IssuePage['summary'][number];
export type LogEntry = Schemas['MigrationLogEntry'];
export type Reconciliation = Schemas['MigrationReconciliation'];
export type ReconSection = Reconciliation['sections'][number];
export type ReconRow = ReconSection['rows'][number];
export type MigrationLink = Schemas['MigrationLink'];
export type OeJournalRow = Schemas['MigrationOeJournalRow'];

export type AsanaParams = Schemas['MigrationAsanaParamsInput'];
export type ClockifyParams = Schemas['MigrationClockifyParamsInput'];
export type UpParams = Schemas['MigrationUpParamsInput'];
export type SheetServicesParams = Schemas['MigrationSheetServicesParamsInput'];
export type SheetOeParams = Schemas['MigrationSheetOeParamsInput'];
export type SheetRegulationsParams = Schemas['MigrationSheetRegulationsParamsInput'];
export type JobCreateInput = Schemas['MigrationJobCreateInput'];
export type JobPatchInput = Schemas['MigrationJobPatchInput'];
export type ApplyInput = Schemas['MigrationApplyRequestInput'];

export type JobsQuery = ApiQuery<'/api/v1/migration/jobs', 'get'>;
export type IssuesQuery = ApiQuery<'/api/v1/migration/jobs/{id}/issues', 'get'>;
export type LinksQuery = ApiQuery<'/api/v1/migration/links', 'get'>;
export type OeJournalQuery = ApiQuery<'/api/v1/migration/oe-journal', 'get'>;

/** Межа файла вивантаження на сервері (files.ts: MAX_FILE_BYTES — 60 МБ) і межа тіла запиту (+1 МБ). */
export const MAX_FILE_BYTES = 60 * 1024 * 1024;
export const MAX_UPLOAD_BODY_BYTES = MAX_FILE_BYTES + 1024 * 1024;

/** Ключі кешу: усе під ['migration']; частини завдання — під ['migration', 'job', id]. */
export const migrationKeys = {
  all: ['migration'] as const,
  probe: ['migration', 'probe'] as const,
  sources: ['migration', 'sources'] as const,
  credentials: ['migration', 'credentials'] as const,
  files: ['migration', 'files'] as const,
  jobsAll: ['migration', 'jobs'] as const,
  jobs: (q: Record<string, unknown>) => ['migration', 'jobs', q] as const,
  job: (id: string) => ['migration', 'job', id] as const,
  runs: (id: string) => ['migration', 'job', id, 'runs'] as const,
  issuesAll: (id: string) => ['migration', 'job', id, 'issues'] as const,
  issues: (id: string, q: Record<string, unknown>) => ['migration', 'job', id, 'issues', q] as const,
  log: (id: string, runId: string | null) => ['migration', 'job', id, 'log', runId] as const,
  reconciliation: (id: string, runId: string | null) => ['migration', 'job', id, 'reconciliation', runId] as const,
  links: (q: Record<string, unknown>) => ['migration', 'links', q] as const,
  oeJournal: (q: Record<string, unknown>) => ['migration', 'oe-journal', q] as const,
};

/** Запуск у черзі чи виконується. Точний тип-предикат: у гілці «ні» запуск лишається MigrationRun, а не never. */
export function isActiveRun(run: MigrationRun | null | undefined): run is MigrationRun & { status: 'queued' | 'running' } {
  return !!run && (run.status === 'queued' || run.status === 'running');
}

/** Модуль підключено на сервері й користувач — адміністратор (для реєстру модулів). */
export function useMigrationAvailable(): boolean {
  const { isAdmin } = useSession();
  return useRouteProbe(migrationKeys.probe, '/api/v1/migration/sources', { enabled: isAdmin }).available;
}

// ——— Джерела й ключі доступу ———

export function useSources() {
  return useQuery({
    queryKey: migrationKeys.sources,
    queryFn: async () => (await call(api.GET('/api/v1/migration/sources'))).data,
    staleTime: 60_000,
  });
}

export function useCredentials() {
  return useQuery({
    queryKey: migrationKeys.credentials,
    queryFn: async () => (await call(api.GET('/api/v1/migration/credentials'))).data,
    staleTime: 30_000,
  });
}

function useCredentialCache() {
  const qc = useQueryClient();
  return (cred: MigrationCredential) => {
    qc.setQueryData<MigrationCredential[]>(migrationKeys.credentials, (list) => (list ? list.map((c) => (c.source === cred.source ? cred : c)) : list));
    void qc.invalidateQueries({ queryKey: migrationKeys.credentials });
    void qc.invalidateQueries({ queryKey: migrationKeys.sources });
  };
}

/** Новий ключ ({ secret }) або налаштування ключа ({ settings } — замінюють попередні повністю). */
export function useSaveCredential() {
  const put = useCredentialCache();
  return useMutation({
    mutationFn: ({ source, body }: { source: CredentialSource; body: CredentialSetInput }) =>
      call(api.PUT('/api/v1/migration/credentials/{source}', { params: { path: { source } }, body })),
    onSuccess: put,
  });
}

export function useRevokeCredential() {
  const put = useCredentialCache();
  return useMutation({
    mutationFn: (source: CredentialSource) => call(api.DELETE('/api/v1/migration/credentials/{source}', { params: { path: { source } } })),
    onSuccess: put,
  });
}

export function useCheckCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (source: CredentialSource) => call(api.POST('/api/v1/migration/credentials/{source}/check', { params: { path: { source } } })),
    // Сервер записує результат останньої перевірки в ключ — оновлюємо стан ключа.
    onSettled: () => void qc.invalidateQueries({ queryKey: migrationKeys.credentials }),
  });
}

// ——— Файли вивантажень ———

export function useFiles(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: migrationKeys.files,
    queryFn: async () => (await call(api.GET('/api/v1/migration/files'))).data,
    enabled: opts.enabled ?? true,
    staleTime: 30_000,
  });
}

export function useUploadFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; content: string }) => call(api.POST('/api/v1/migration/files', { body })),
    onSuccess: (file) => {
      qc.setQueryData<MigrationFile[]>(migrationKeys.files, (list) => (list ? [file, ...list.filter((f) => f.id !== file.id)] : list));
      void qc.invalidateQueries({ queryKey: migrationKeys.files });
    },
  });
}

export function useDeleteFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => call(api.DELETE('/api/v1/migration/files/{id}', { params: { path: { id } } })),
    onSuccess: (_r, id) => {
      qc.setQueryData<MigrationFile[]>(migrationKeys.files, (list) => (list ? list.filter((f) => f.id !== id) : list));
      void qc.invalidateQueries({ queryKey: migrationKeys.files });
    },
  });
}

// ——— Завдання імпорту ———

/** Перелік завдань (курсор); поки хоч одне завдання виконується — оновлення кожні 4 с (поступ у переліку). */
export function useJobs(filter: { source?: MigrationSourceKey | null; includeArchived?: boolean }) {
  const query = useMemo<JobsQuery>(
    () => ({ limit: 50, ...(filter.source ? { source: filter.source } : {}), ...(filter.includeArchived ? { includeArchived: 'true' as const } : {}) }),
    [filter.source, filter.includeArchived],
  );
  const q = useInfiniteQuery({
    queryKey: migrationKeys.jobs(query),
    queryFn: ({ pageParam }) => call(api.GET('/api/v1/migration/jobs', { params: { query: { ...query, ...(pageParam ? { cursor: pageParam } : {}) } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    refetchInterval: (query) => (query.state.data?.pages.some((p) => p.data.some((j) => isActiveRun(j.lastRun))) ? 4000 : false),
  });
  const items = useMemo(() => q.data?.pages.flatMap((p) => p.data) ?? [], [q.data]);
  return {
    items,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    error: q.error,
    refetch: q.refetch,
    hasMore: !!q.hasNextPage,
    loadMore: () => void q.fetchNextPage(),
    loadingMore: q.isFetchingNextPage,
  };
}

/** Завдання зі станом останнього запуску; поки запуск у черзі чи виконується — опитування кожні 2 с. */
export function useJob(id: string | undefined) {
  return useQuery({
    queryKey: migrationKeys.job(id ?? '—'),
    enabled: !!id,
    queryFn: () => call(api.GET('/api/v1/migration/jobs/{id}', { params: { path: { id: id! } } })),
    refetchInterval: (q) => (isActiveRun(q.state.data?.lastRun) ? 2000 : false),
    staleTime: 5_000,
  });
}

export function useRuns(jobId: string | undefined) {
  return useQuery({
    queryKey: migrationKeys.runs(jobId ?? '—'),
    enabled: !!jobId,
    queryFn: async () => (await call(api.GET('/api/v1/migration/jobs/{id}/runs', { params: { path: { id: jobId! } } }))).data,
    staleTime: 10_000,
  });
}

function useJobCache() {
  const qc = useQueryClient();
  return (job: MigrationJob) => {
    qc.setQueryData(migrationKeys.job(job.id), job);
    void qc.invalidateQueries({ queryKey: migrationKeys.job(job.id) });
    void qc.invalidateQueries({ queryKey: migrationKeys.jobsAll });
  };
}

export function useCreateJob() {
  const put = useJobCache();
  return useMutation({
    mutationFn: (body: JobCreateInput) => call(api.POST('/api/v1/migration/jobs', { body })),
    onSuccess: put,
  });
}

/** Назва чи параметри (параметри повністю замінюють попередні); If-Match — за версією завдання. */
export function usePatchJob() {
  const put = useJobCache();
  return useMutation({
    mutationFn: ({ id, version, body }: { id: string; version: number; body: JobPatchInput }) =>
      call(api.PATCH('/api/v1/migration/jobs/{id}', { params: { path: { id } }, body, headers: ifMatch(version) })),
    onSuccess: put,
  });
}

export function useArchiveJob() {
  const put = useJobCache();
  return useMutation({
    mutationFn: (id: string) => call(api.DELETE('/api/v1/migration/jobs/{id}', { params: { path: { id } } })),
    onSuccess: put,
  });
}

/** Після постановки запуску в чергу: запуск одразу в стані завдання (щоб опитування почалося без затримки). */
function useRunQueued() {
  const qc = useQueryClient();
  return (run: MigrationRun) => {
    qc.setQueryData<MigrationJob>(migrationKeys.job(run.jobId), (j) => (j ? { ...j, lastRun: run } : j));
    void qc.invalidateQueries({ queryKey: migrationKeys.job(run.jobId) });
    void qc.invalidateQueries({ queryKey: migrationKeys.jobsAll });
  };
}

export function useStartDryRun() {
  const queued = useRunQueued();
  return useMutation({
    mutationFn: (jobId: string) => call(api.POST('/api/v1/migration/jobs/{id}/dry-run', { params: { path: { id: jobId } } })),
    onSuccess: queued,
  });
}

export function useApply() {
  const queued = useRunQueued();
  return useMutation({
    mutationFn: ({ jobId, body }: { jobId: string; body: ApplyInput }) => call(api.POST('/api/v1/migration/jobs/{id}/apply', { params: { path: { id: jobId } }, body })),
    onSuccess: queued,
  });
}

export function useCancelRun() {
  const queued = useRunQueued();
  return useMutation({
    mutationFn: (jobId: string) => call(api.POST('/api/v1/migration/jobs/{id}/cancel', { params: { path: { id: jobId } } })),
    onSuccess: queued,
  });
}

// ——— Проблеми, журнал, звірка ———

/** Реєстр проблем запуску (курсор) із зведенням за кодами й важливістю (зведення — за весь запуск). */
export function useIssues(jobId: string, filter: Omit<IssuesQuery, 'cursor' | 'limit'>, opts: { poll?: boolean; enabled?: boolean } = {}) {
  const query = useMemo(() => ({ limit: 50, ...filter }), [filter]);
  const q = useInfiniteQuery({
    queryKey: migrationKeys.issues(jobId, query),
    queryFn: ({ pageParam }) =>
      call(api.GET('/api/v1/migration/jobs/{id}/issues', { params: { path: { id: jobId }, query: { ...query, ...(pageParam ? { cursor: pageParam } : {}) } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled: opts.enabled ?? true,
    refetchInterval: opts.poll ? 4000 : false,
    staleTime: 10_000,
  });
  const pages = q.data?.pages;
  const items = useMemo(() => pages?.flatMap((p) => p.data) ?? [], [pages]);
  return {
    items,
    summary: pages?.[0]?.summary ?? [],
    runId: pages?.[0]?.runId ?? null,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    isPlaceholder: q.isPlaceholderData,
    error: q.error,
    refetch: q.refetch,
    hasMore: !!q.hasNextPage,
    loadMore: () => void q.fetchNextPage(),
    loadingMore: q.isFetchingNextPage,
  };
}

export function useResolveIssue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ issueId, note }: { issueId: string; jobId: string; note?: string }) =>
      call(api.POST('/api/v1/migration/issues/{id}/resolve', { params: { path: { id: issueId } }, body: note ? { note } : {} })),
    onSuccess: (_i, v) => void qc.invalidateQueries({ queryKey: migrationKeys.issuesAll(v.jobId) }),
  });
}

/** Журнал запуску: курсор «after» (номер запису) вперед; під час виконання — оновлення кожні 4 с. */
export function useRunLog(jobId: string, runId: string | null, opts: { poll?: boolean } = {}) {
  const q = useInfiniteQuery({
    queryKey: migrationKeys.log(jobId, runId),
    queryFn: ({ pageParam }) =>
      call(
        api.GET('/api/v1/migration/jobs/{id}/log', {
          params: { path: { id: jobId }, query: { limit: 100, ...(runId ? { runId } : {}), ...(pageParam ? { after: pageParam } : {}) } },
        }),
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: opts.poll ? 4000 : false,
    staleTime: 10_000,
  });
  const items = useMemo(() => q.data?.pages.flatMap((p) => p.data) ?? [], [q.data]);
  return {
    items,
    isLoading: q.isLoading,
    error: q.error,
    refetch: q.refetch,
    hasMore: !!q.hasNextPage,
    loadMore: () => void q.fetchNextPage(),
    loadingMore: q.isFetchingNextPage,
  };
}

/** Протокол звірки: типово — останнє застосування (або останній запуск). */
export function useReconciliation(jobId: string, runId: string | null, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: migrationKeys.reconciliation(jobId, runId),
    enabled: opts.enabled ?? true,
    queryFn: () => call(api.GET('/api/v1/migration/jobs/{id}/reconciliation', { params: { path: { id: jobId }, query: runId ? { runId } : {} } })),
    staleTime: 15_000,
  });
}

// ——— Старі посилання й архів ОЕ ———

export type LinkLookup = Omit<LinksQuery, 'limit'>;

/** Кілька пошуків разом (напр. ідентифікатор і як об’єкт джерела, і як об’єкт ЄРС); результати без повторів. */
export function useLinkLookup(lookups: LinkLookup[]) {
  return useQuery({
    queryKey: migrationKeys.links({ lookups }),
    enabled: lookups.length > 0,
    queryFn: async () => {
      const pages = await Promise.all(lookups.map((q) => call(api.GET('/api/v1/migration/links', { params: { query: { limit: 50, ...q } } }))));
      const seen = new Map<string, MigrationLink>();
      for (const p of pages)
        for (const l of p.data) {
          const key = `${l.source}|${l.sourceType}|${l.sourceId}`;
          if (!seen.has(key)) seen.set(key, l);
        }
      return [...seen.values()];
    },
    staleTime: 30_000,
  });
}

export function useOeJournal(filter: { from?: string | null; to?: string | null }) {
  const query = useMemo<OeJournalQuery>(() => ({ limit: 50, ...(filter.from ? { from: filter.from } : {}), ...(filter.to ? { to: filter.to } : {}) }), [filter.from, filter.to]);
  return useCursorQuery<OeJournalRow>(migrationKeys.oeJournal(query), async (cursor) =>
    call(api.GET('/api/v1/migration/oe-journal', { params: { query: { ...query, ...(cursor ? { cursor } : {}) } } })),
  );
}
